if (!customElements.get('sticky-anchor-navigation')) {
  class StickyAnchorNavigation extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.sticky-anchor-navigation__item'));
      this.anchorItems = this.items.filter((item) => item.dataset.targetId);
      this.mobileMediaQuery = window.matchMedia('(max-width: 849px)');
      this.handleBreakpointChange = () => this.createObserver();
      this.extraOffset = parseInt(this.dataset.extraOffset || '0', 10) || 0;

      this.anchorItems.forEach((item) => {
        item.addEventListener('click', (event) => this.handleClick(event, item));
      });

      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.handleBreakpointChange);
      } else if (this.mobileMediaQuery.addListener) {
        this.mobileMediaQuery.addListener(this.handleBreakpointChange);
      }

      this.createObserver();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }

      if (this.mobileMediaQuery && this.handleBreakpointChange) {
        if (this.mobileMediaQuery.removeEventListener) {
          this.mobileMediaQuery.removeEventListener('change', this.handleBreakpointChange);
        } else if (this.mobileMediaQuery.removeListener) {
          this.mobileMediaQuery.removeListener(this.handleBreakpointChange);
        }
      }
    }

    createObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      this.targets = this.anchorItems
        .map((item) => this.getTarget(item))
        .filter(Boolean);

      if (!this.targets.length) {
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const activeItem = this.anchorItems.find((item) => this.getTarget(item) === entry.target);

          if (activeItem) {
            this.setActiveItem(activeItem);
          }
        });
      }, {
        rootMargin: `-${this.getScrollOffset() + 1}px 0px -60% 0px`,
        threshold: 0
      });

      this.targets.forEach((target) => this.observer.observe(target));
    }

    handleClick(event, item) {
      event.preventDefault();
      this.setActiveItem(item);

      const target = this.getTarget(item);

      if (!target) {
        return;
      }

      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset(),
        left: 0,
        behavior: 'smooth'
      });

      if (history.pushState) {
        history.pushState(null, '', `#${target.id}`);
      }
    }

    getTarget(item) {
      const desktopTargetId = item.dataset.targetId || '';
      const mobileTargetId = item.dataset.targetIdMobile || '';

      if (this.mobileMediaQuery.matches && mobileTargetId) {
        const mobileTarget = this.findTarget(mobileTargetId);

        if (mobileTarget) {
          return mobileTarget;
        }
      }

      return this.findTarget(desktopTargetId);
    }

    findTarget(targetId) {
      const normalizedTargetId = this.normalizeTargetId(targetId);

      if (!normalizedTargetId) {
        return null;
      }

      const exactTarget = document.getElementById(normalizedTargetId);

      if (exactTarget) {
        return exactTarget;
      }

      const stableShopifyId = this.getStableShopifySectionId(normalizedTargetId);

      if (!stableShopifyId) {
        return null;
      }

      return Array.from(document.querySelectorAll('[id]')).find((element) => element.id.endsWith(`__${stableShopifyId}`)) || null;
    }

    normalizeTargetId(targetId) {
      return (targetId || '').trim().replace(/^#/, '');
    }

    getStableShopifySectionId(targetId) {
      const cleanTargetId = targetId.replace(/^shopify-section-/, '');
      const parts = cleanTargetId.split('__');

      return parts.length > 1 ? parts.pop() : cleanTargetId;
    }

    setActiveItem(activeItem) {
      this.items.forEach((item) => item.classList.toggle('active', item === activeItem));
      this.scrollItemIntoView(activeItem);
    }

    scrollItemIntoView(item) {
      const scroller = this.querySelector('.sticky-anchor-navigation__scroller');

      if (!scroller) {
        return;
      }

      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      const viewLeft = scroller.scrollLeft;
      const viewRight = viewLeft + scroller.offsetWidth;

      if (itemLeft < viewLeft || itemRight > viewRight) {
        scroller.scrollTo({
          left: itemLeft - (scroller.offsetWidth - item.offsetWidth) / 2,
          behavior: 'smooth'
        });
      }
    }

    getScrollOffset() {
      const headerHeight = document.body.classList.contains('header-sticky--active')
        ? parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0
        : 0;

      return headerHeight + this.offsetHeight + this.extraOffset;
    }
  }

  customElements.define('sticky-anchor-navigation', StickyAnchorNavigation);
}
